import React, { useEffect, useMemo, useState } from "react";
import { fetchLotteries } from "../api";
import LotteryLogo from "./LotteryLogo";

/**
 * The 20 specific draws to display in the carousel banner, in order.
 * Each entry maps to a (lottery_id, draw_id) and provides the colored
 * brand pill label/colors that match the user's reference design.
 */
const CAROUSEL_DRAWS = [
  { lid: "nacional",              did: "gana-mas",            brand: "Nacional",     color: "#16a34a", text: "#ffffff" },
  { lid: "nacional",              did: "quiniela",            brand: "Nacional",     color: "#16a34a", text: "#ffffff" },
  { lid: "leidsa",                did: "quiniela-pale",       brand: "Leidsa",       color: "#fde047", text: "#1f2937" },
  { lid: "loto-real",             did: "quiniela",            brand: "Real",         color: "#1d4ed8", text: "#fde047" },
  { lid: "loteka",                did: "quiniela-mega-decenas", brand: "Loteka",     color: "#38bdf8", text: "#0c4a6e" },
  { lid: "americanas",            did: "florida-tarde",       brand: "Americanas",   color: "#1e1b4b", text: "#ffffff" },
  { lid: "americanas",            did: "florida-noche",       brand: "Americanas",   color: "#312e81", text: "#ffffff" },
  { lid: "americanas",            did: "new-york-medio-dia",  brand: "Americanas",   color: "#2563eb", text: "#ffffff" },
  { lid: "americanas",            did: "new-york-noche",      brand: "Americanas",   color: "#2563eb", text: "#ffffff" },
  { lid: "la-primera",            did: "quiniela-medio-dia",  brand: "La Primera",   color: "#dc2626", text: "#ffffff" },
  { lid: "la-primera",            did: "quiniela-noche",      brand: "La Primera",   color: "#dc2626", text: "#ffffff" },
  { lid: "la-suerte-dominicana",  did: "quiniela",            brand: "La Suerte",    color: "#1e3a8a", text: "#fde047" },
  { lid: "la-suerte-dominicana",  did: "quiniela-tarde",      brand: "La Suerte",    color: "#1e3a8a", text: "#fde047" },
  { lid: "lotedom",               did: "quiniela",            brand: "LoteDom",      color: "#1e1b4b", text: "#fde047" },
  { lid: "anguila",               did: "anguila-manana",      brand: "Anguila",      color: "#f97316", text: "#ffffff" },
  { lid: "anguila",               did: "anguila-medio-dia",   brand: "Anguila",      color: "#f97316", text: "#ffffff" },
  { lid: "anguila",               did: "anguila-tarde",       brand: "Anguila",      color: "#f97316", text: "#ffffff" },
  { lid: "anguila",               did: "anguila-noche",       brand: "Anguila",      color: "#f97316", text: "#ffffff" },
  { lid: "king-lottery",          did: "quiniela-dia",        brand: "King Lottery", color: "#1d4ed8", text: "#fde047" },
  { lid: "king-lottery",          did: "quiniela-noche",      brand: "King Lottery", color: "#1d4ed8", text: "#fde047" },
];

function formatDate() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function isTodayDraw(drawTime) {
  if (!drawTime || !/^\d{1,2}:\d{2}$/.test(drawTime)) return false;
  try {
    const now = new Date();
    const drNow = new Date(now.getTime() + (now.getTimezoneOffset() - 240) * 60000);
    const [h, m] = drawTime.split(":").map((v) => parseInt(v, 10));
    const drMinutes = drNow.getHours() * 60 + drNow.getMinutes();
    const drawMinutes = h * 60 + m;
    return drMinutes >= drawMinutes;
  } catch {
    return false;
  }
}

function Ball({ n, today }) {
  const cls = today
    ? "from-green-500 to-green-600 border-green-700 text-white"
    : "from-gray-300 to-gray-400 border-gray-500 text-gray-700";
  return (
    <span
      className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br ${cls} font-extrabold text-lg shadow-md border-2`}
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {n}
    </span>
  );
}

function Card({ entry, lottery, draw, latest }) {
  const date = formatDate();
  const numbers = (latest && latest.numbers) || [];
  const showNums = numbers.length > 0 ? numbers.slice(0, 3) : ["--", "--", "--"];
  const today = isTodayDraw(draw && draw.time);

  return (
    <div className="w-[280px] flex-shrink-0">
      {/* Colored brand pill */}
      <div
        className="rounded-full text-center font-extrabold text-lg py-2 px-4 shadow-sm tracking-wide underline underline-offset-2"
        style={{ background: entry.color, color: entry.text }}
      >
        {entry.brand}
      </div>

      {/* Card body */}
      <div className="mt-2 rounded-2xl bg-gray-200/90 px-4 pt-3 pb-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-semibold text-gray-600">{date}</span>
          {(draw && draw.logo) || (lottery && lottery.logo) ? (
            <LotteryLogo lottery={lottery} draw={draw} size={36} />
          ) : null}
        </div>
        <p
          className="mt-1 text-center text-xl font-extrabold text-gray-800 truncate"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {(draw && draw.name) || entry.brand}
        </p>
        <div className="mt-3 flex items-center justify-around gap-2">
          {showNums.map((n, i) => (
            <Ball key={i} n={n} today={today} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ResultsCarousel() {
  const [lotteries, setLotteries] = useState([]);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await fetchLotteries();
        if (mounted) setLotteries(data.lotteries || []);
      } catch {
        // keep previous
      }
    };
    load();
    const t = setInterval(load, 3 * 60 * 1000); // refresh every 3 minutes
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const lookups = useMemo(() => {
    const out = {};
    lotteries.forEach((l) => {
      l.draws.forEach((d) => {
        out[`${l.id}/${d.id}`] = {
          lottery: l,
          draw: d,
          latest: (l.latest || {})[d.id] || null,
        };
      });
    });
    return out;
  }, [lotteries]);

  const cards = useMemo(() => {
    return CAROUSEL_DRAWS.map((entry) => {
      const data = lookups[`${entry.lid}/${entry.did}`];
      return { entry, ...(data || {}) };
    });
  }, [lookups]);

  // Duplicate the list to make the marquee loop seamlessly
  const loop = [...cards, ...cards];

  return (
    <section className="py-6 px-4 bg-gradient-to-b from-blue-50/40 to-white border-y border-blue-100">
      <div className="max-w-7xl mx-auto overflow-hidden"
           onMouseEnter={() => setPaused(true)}
           onMouseLeave={() => setPaused(false)}>
        <div
          className="flex gap-5 np-marquee-track"
          style={{ animationPlayState: paused ? "paused" : "running" }}
        >
          {loop.map((c, idx) => (
            <Card key={idx} entry={c.entry} lottery={c.lottery} draw={c.draw} latest={c.latest} />
          ))}
        </div>
      </div>
    </section>
  );
}
